// Findings #91 / #92: day rows that hold nothing.
//
// A Base de datos row is only a (day, lot) identity; its values come from the
// records overlaid on it (feed, mortality, sampling, stock, harvest) or typed
// into it. When the last record of a day is deleted (Corregir → Eliminar), or
// the lot itself is deleted, the bare row stays behind and shows as a date
// with only "–" (or as "lote sin mapear"). User decision 2026-09-30: HIDE such
// rows, delete nothing — any row holding a record always shows (traceability),
// and today's rows stay visible (Crew's today row, a row just added).
//
// Hidden rows never affected a figure: the mortality register counts records,
// the projections count days with a mortality value, the Panel rate skips
// blank rows in both parts of its ratio.

import { toLocalCalendarDayKey } from './dateNormalization'
import { farmDayKey } from './farmTime'

/** Row identity and labels — never "data". */
const IDENTITY_KEYS = new Set([
  'id', 'date', 'lot', 'lotId', 'unmappedLot', 'stage', 'archived',
  'sourceKey', 'farmId', 'productionUnitId', 'farmName', 'productionUnitName',
  'lotUnit', 'lotOrigin', 'createdAt', 'updatedAt', 'pk', 'tenantKey'
])

/** Values derived from OTHER rows (carried-forward stock, daily rate). */
const DERIVED_KEYS = new Set(['fishStockDerived', 'mortalityRatePct'])

/** True when the row carries any value or record link of its own. */
export const productionRowHoldsData = (row: Record<string, unknown>): boolean => (
  Object.entries(row).some(([key, value]) => {
    if (IDENTITY_KEYS.has(key) || DERIVED_KEYS.has(key)) return false
    if (value === null || value === undefined) return false
    if (typeof value === 'object') return false
    return String(value).trim() !== ''
  })
)

export type EmptyDayRowContext = {
  /** The farm's today (YYYY-MM-DD). */
  today: string
  timeZone: string
  /**
   * Whether the row's lot still exists; `undefined` while lots are loading
   * (the lot rule is then skipped so nothing disappears by accident).
   */
  lotExists?: (row: Record<string, unknown>) => boolean
}

/**
 * Hide an empty row when its lot no longer exists (#92) or its farm day is
 * before today (#91). A row holding anything is never hidden.
 */
export const isHiddenEmptyDayRow = (row: Record<string, unknown>, context: EmptyDayRowContext): boolean => {
  if (productionRowHoldsData(row)) return false
  if (context.lotExists && !context.lotExists(row)) return true
  const day = farmDayKey(row.date, context.timeZone)
  return day !== null && day < context.today
}

/** A lot-existence check from the current lots (soft-deleted lots are not listed). */
export const buildLotExists = (lots: Array<{ id: string; lotKey?: string }>) => {
  const ids = new Set(lots.map((lot) => String(lot.id)))
  const keys = new Set(lots.map((lot) => String(lot.lotKey ?? '').trim()).filter(Boolean))
  return (row: Record<string, unknown>): boolean => {
    const lotId = typeof row.lotId === 'string' ? row.lotId : ''
    if (lotId) return ids.has(lotId)
    const lotKey = typeof row.lot === 'string' ? row.lot.trim() : ''
    return lotKey ? keys.has(lotKey) : true
  }
}

// Findings #192: a crew capture (mortality, weight sample) adds an empty row for
// its day when the phone has not yet received the office's row for that lot and
// day (crewMode/ensureTodayProductionRow). The count and the mortality were then
// overlaid on both rows — the lot looked counted twice and its mortality doubled
// — though each record exists once. Applied to the stored rows BEFORE the
// overlays: when a unit has a row holding data for a lot and day, the empty rows
// of that lot and day are left out. Display only — nothing is deleted; if every
// row of the day is empty, the first one stays.
const sameDayGroupKey = (row: Record<string, unknown>): string => {
  const day = toLocalCalendarDayKey(typeof row.date === 'string' ? row.date : '')
  const lot = typeof row.lot === 'string' ? row.lot.trim() : ''
  if (!day || !lot) return ''
  return `${String(row.productionUnitId ?? '')}|${day}|${lot}`
}

export const dropEmptySameDayDuplicateRows = <T extends object>(rows: T[]): T[] => {
  if (rows.length < 2) return rows
  const groups = new Map<string, { holdsData: boolean; firstEmpty: T | null }>()
  for (const row of rows) {
    const key = sameDayGroupKey(row as Record<string, unknown>)
    if (!key) continue
    const group = groups.get(key) ?? { holdsData: false, firstEmpty: null }
    if (productionRowHoldsData(row as Record<string, unknown>)) group.holdsData = true
    else if (group.firstEmpty === null) group.firstEmpty = row
    groups.set(key, group)
  }
  const kept = rows.filter((row) => {
    const key = sameDayGroupKey(row as Record<string, unknown>)
    const group = key ? groups.get(key) : undefined
    if (!group || productionRowHoldsData(row as Record<string, unknown>)) return true
    return !group.holdsData && group.firstEmpty === row
  })
  return kept.length === rows.length ? rows : kept
}
